import { useNavigate, useParams } from "react-router";
import {
	CustomQuoteDetailContent,
	CustomQuoteDetailNav,
	CustomQuoteDetailNavItem,
	CustomQuoteDetailPageTop,
	CustomQuoteDetailPageWrapper,
	CustomQuoteErrorBoxWrapper
} from "./CustomQuoteDetailPage.styles";
import { Link } from "components/Common/Link";
import {
	AssigneeText,
	BackText,
	CustomQuotesHeaderText,
	NotFoundHeader,
	NotFoundText,
	QuoterErrorText,
	RequestDateText,
	ReturnToCustomQuoteButtonText,
	ServiceUnavailableHeader,
	ServiceUnavailableText,
	UnavailableDataPlaceholderText
} from "constants/text";
import { Button, FormControl, Skeleton } from "@mui/material";
import theme from "styles/themes/variants/defaultTheme";
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import { CustomQuoteDetailHeader } from "components/CustomQuote/CustomQuoteDetail/CustomQuoteDetailHeader.tsx";
import { CustomQuoteHistory } from "components/CustomQuote/CustomQuoteDetail/CustomQuoteDetailHistory.tsx";
import { CustomQuoteDetail } from "components/CustomQuote/CustomQuoteDetail/CustomQuoteDetail.tsx";
import {
	useCreateRevisionMutation,
	useGetCustomQuoteByIdQuery,
	useGetQuotersQuery,
	useUpdateCustomQuoteMutation
} from "features/api/customQuotesApi.ts";
import AutoComplete from "components/Common/Autocomplete/Autocomplete.tsx";
import React, { useCallback, useEffect, useState } from "react";
import { AddCustomQuoteText, DeclineRequest } from "components/NewOrders/constants.ts";
import { useGetUserDetailsQuery } from "features/api/userApi.ts";
import { useDialog } from "hooks/useDialog.ts";
import { DialogTracking } from "components/Dialog/components/DialogTracking.ts";
import { UserRole } from "types/declarations/enums.ts";
import { models } from "types/api/viewModels.ts";
import { APIErrorFaceBox } from "components/Common/APIErrorFaceBox/APIErrorFaceBox.tsx";
import { sortByTimestampDesc } from "components/CustomQuote/customQuoteUtils";

export const CustomQuoteDetailPage = () => {
	const { quoteId } = useParams();
	const navigate = useNavigate();
	const [selectedAssignee, setSelectedAssignee] = useState<any>(null);
	const { openDialog } = useDialog();
	const [isAddingRevision, setIsAddingRevision] = useState(false);

	const {
		data: customQuoteData,
		error: customQuoteDataError,
		isLoading: customQuoteDataLoading
	} = useGetCustomQuoteByIdQuery(quoteId ?? "");
	const [updateCustomQuote, { error: updateCustomQuoteError, isLoading: updateCustomQuoteLoading }] =
		useUpdateCustomQuoteMutation();
	const [createRevision] = useCreateRevisionMutation();
	const { data: quoters } = useGetQuotersQuery("");
	const { data: userDetails } = useGetUserDetailsQuery();
	const userRole = userDetails?.role;

	const handleAssignChange = useCallback(
		(assignedToId: string | null) => {
			if (quoteId && assignedToId) {
				void updateCustomQuote({ id: quoteId, assignedToId });
			}
		},
		[quoteId, updateCustomQuote]
	);

	const addCustomQuote = () => {
		setIsAddingRevision(true);
		const revision: models["CreateRevisionRequest"] = {
			lineItem: customQuoteData?.lineItem,
			globals: customQuoteData?.globals
		};
		createRevision({ quoteId: quoteId ?? "", revision })
			.then((response) => {
				const revisions = response?.data?.revisions ?? [];
				const sortedRevisions = sortByTimestampDesc(revisions);
				const revisionId = sortedRevisions?.[0]?.revisionId ?? 0;
				navigate(`/custom-quote/${quoteId}/add-quote?revisionId=${revisionId}`);
			})
			.catch(() => {
				setIsAddingRevision(false);
			});
	};

	useEffect(() => {
		if (quoters?.length && userRole === UserRole.QUOTER && customQuoteData?.assignedToId) {
			setSelectedAssignee(quoters.find((q) => q.userId === customQuoteData.assignedToId));
		}
	}, [customQuoteData?.assignedToId, quoters, selectedAssignee, userRole]);

	const handleDeclineRequest = () => {
		openDialog({
			dialogStatus: DialogTracking.DECLINE_CUSTOM_QUOTE,
			dialogId: quoteId
		});
	};

	const isLoading = customQuoteDataLoading || updateCustomQuoteLoading || isAddingRevision;

	if (customQuoteDataError) {
		const statusInCustomQuoteDataError = "status" in customQuoteDataError;
		const is404Error = statusInCustomQuoteDataError && customQuoteDataError.status === 404;
		return (
			<CustomQuoteErrorBoxWrapper>
				<APIErrorFaceBox
					buttonText={ReturnToCustomQuoteButtonText}
					buttonClick={() => navigate("/custom-quotes")}
					headerText={is404Error ? NotFoundHeader : ServiceUnavailableHeader}
					descriptionText={is404Error ? NotFoundText : ServiceUnavailableText}
					errorCode={statusInCustomQuoteDataError ? customQuoteDataError.status : null}
					faceBoxType={is404Error ? "notFound" : "generic"}
				/>
			</CustomQuoteErrorBoxWrapper>
		);
	}

	return (
		<CustomQuoteDetailPageWrapper variant="orders">
			<CustomQuoteDetailPageTop variant="panel">
				<CustomQuoteDetailNav>
					<CustomQuoteDetailNavItem>
						<div>
							{isLoading ? (
								<Skeleton
									width="97px"
									height="24px"
									variant="rectangular"
								/>
							) : (
								<Link
									to="/custom-quote"
									hasBackIcon
									data-testid="custom-quote-details-back-link"
								>
									{BackText}
								</Link>
							)}
						</div>
						{!isLoading && (
							<div
								className="overline"
								style={{ color: theme.project.colors.fontColors.secondary }}
								data-testid="custom-quote-request-date"
							>
								{RequestDateText}:
								{customQuoteData?.createdDate
									? new Date(customQuoteData.createdDate).toLocaleDateString()
									: UnavailableDataPlaceholderText}
							</div>
						)}
						<h2 data-testid="custom-quote-request-name">
							{customQuoteData?.requestName ?? CustomQuotesHeaderText}
						</h2>
					</CustomQuoteDetailNavItem>
					{!isLoading && (
						<CustomQuoteDetailNavItem>
							{userRole === UserRole.QUOTER && customQuoteData?.status !== "Declined" && (
								<>
									<FormControl
										size="small"
										required
										fullWidth
										sx={{ alignSelf: "flex-start" }}
									>
										<AutoComplete
											options={quoters ?? []}
											getOptionLabel={(option: any) =>
												option.firstName && option.lastName
													? `${option.firstName} ${option.lastName}`
													: ""
											}
											onChange={(event, newValue) => {
												setSelectedAssignee(newValue);
												handleAssignChange(newValue?.userId ?? null);
											}}
											value={selectedAssignee}
											isError={Boolean(updateCustomQuoteError)}
											errorText={QuoterErrorText}
											label={AssigneeText}
											dataTestId="customQuote-select-assignee"
											required
											disableClearable
										/>
									</FormControl>
									<Button
										variant="contained"
										startIcon={<AddIcon sx={{ marginBottom: "2px" }} />}
										sx={{ alignItems: "center" }}
										onClick={addCustomQuote}
										fullWidth
									>
										{AddCustomQuoteText}
									</Button>
									<Button
										startIcon={<CancelIcon />}
										sx={{ textTransform: "uppercase" }}
										fullWidth
										data-testid="custom-quote-decline-button"
										onClick={handleDeclineRequest}
									>
										{DeclineRequest}
									</Button>
								</>
							)}
						</CustomQuoteDetailNavItem>
					)}
				</CustomQuoteDetailNav>
				{isLoading ? (
					<Skeleton
						height="120px"
						variant="rectangular"
					/>
				) : (
					<CustomQuoteDetailHeader quote={customQuoteData} />
				)}
			</CustomQuoteDetailPageTop>
			<CustomQuoteDetailContent>
				{!isLoading && (
					<CustomQuoteHistory
						revisions={customQuoteData?.revisions}
						userRole={userRole}
						customQuoteData={customQuoteData}
					/>
				)}
				<CustomQuoteDetail
					quote={customQuoteData}
					isLoading={isLoading}
				/>
			</CustomQuoteDetailContent>
		</CustomQuoteDetailPageWrapper>
	);
};
